<script setup>
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import {
  FILE_FORMATS,
  OUTPUT_FILES,
  downloadFile,
  fileName,
} from "@/utils/downloadMake";
import { parseBlocks, toPlainText } from "@/utils/markdown";
import { parseSlides } from "@/utils/slides";

// A small Download pill for the result's header: one tap saves the make as its own kind
// of file, and the arrow shows what the file is and the other formats it comes in
const props = defineProps({
  output: { type: String, required: true },
  text: { type: String, default: "" },
  title: { type: String, default: "" },
  image: { type: String, default: "" },
  // In the slim pinned header a phone only has room for the icon
  compact: { type: Boolean, default: false },
});

const KINDS = {
  docx: { name: "Word document", icon: "file-word", colour: "#2B579A" },
  pdf: { name: "PDF", icon: "file-pdf", colour: "#D93025" },
  xlsx: { name: "Spreadsheet", icon: "file-excel", colour: "#217346" },
  pptx: { name: "PowerPoint", icon: "file-powerpoint", colour: "#C43E1C" },
  image: { name: "Picture", icon: "image", colour: "#CF5AE2" },
  text: { name: "Text", icon: "align-left", colour: "#756E94" },
};

const kind = computed(() => KINDS[props.output] || KINDS.text);
const file = computed(() => OUTPUT_FILES[props.output] || OUTPUT_FILES.text);
const name = computed(() =>
  fileName(props.title, FILE_FORMATS[file.value.format].extension)
);
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const details = computed(() => {
  if (props.output === "image") return "PNG picture";
  if (props.output === "pptx") {
    return plural(parseSlides(props.text).slides.length + 1, "slide");
  }
  if (props.output === "xlsx") {
    const tables = parseBlocks(props.text).filter(
      (block) => block.type === "table"
    ).length;
    return plural(Math.max(tables, 1), "sheet");
  }
  const words = toPlainText(props.text).split(/\s+/).filter(Boolean).length;
  return plural(words, "word");
});
const others = computed(() => file.value.others);

const busy = ref(false);
const error = ref("");
const menuOpen = ref(false);
const root = ref(null);
onClickOutside(root, () => (menuOpen.value = false));

const save = async (format) => {
  menuOpen.value = false;
  busy.value = true;
  error.value = "";
  try {
    await downloadFile({
      format,
      text: props.text,
      title: props.title,
      image: props.image,
      plain: props.output === "text" || props.output === "image",
    });
  } catch (failure) {
    console.error(failure);
    error.value = "We couldn't create the file, please try again";
  }
  busy.value = false;
};
</script>

<template>
  <div
    id="shepherd-target-file"
    ref="root"
    class="relative"
    @keydown.esc="menuOpen = false"
  >
    <div class="flex">
      <button
        type="button"
        class="flex items-center rounded-l-full bg-accentPrimary py-2 pl-4 pr-3 text-sm font-bold text-white transition-colors hover:bg-accentPrimaryLight disabled:cursor-wait disabled:opacity-70"
        :class="{ 'rounded-r-full pr-4': !others.length }"
        :disabled="busy"
        :title="`Download ${name}`"
        aria-label="Download"
        @click="save(file.format)"
      >
        <font-awesome-icon
          :icon="['fas', 'download']"
          :class="compact ? 'xs:mr-2' : 'mr-2'"
        />
        <span :class="{ 'hidden xs:inline': compact }">{{
          busy ? "Preparing..." : "Download"
        }}</span>
      </button>
      <button
        v-if="others.length"
        type="button"
        class="flex items-center rounded-r-full border-l border-white/30 bg-accentPrimary py-2 pl-2 pr-3 text-white transition-colors hover:bg-accentPrimaryLight"
        :aria-expanded="menuOpen"
        aria-haspopup="menu"
        title="More formats"
        @click="menuOpen = !menuOpen"
      >
        <font-awesome-icon
          :icon="['fas', 'chevron-down']"
          class="text-xs transition-transform"
          :class="{ 'rotate-180': menuOpen }"
        />
        <span class="sr-only">More formats</span>
      </button>
    </div>
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="-translate-y-1 opacity-0"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="-translate-y-1 opacity-0"
    >
      <div
        v-if="menuOpen"
        class="absolute right-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div class="flex items-center gap-3 border-b border-gray-100 p-3">
          <span
            class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl"
            :style="{ backgroundColor: `${kind.colour}1A`, color: kind.colour }"
          >
            <img
              v-if="output === 'image' && image"
              :src="image"
              alt=""
              class="h-full w-full object-cover"
            />
            <font-awesome-icon v-else :icon="['fas', kind.icon]" />
          </span>
          <span class="min-w-0">
            <span
              class="block truncate text-sm font-bold text-primary"
              :title="name"
              >{{ name }}</span
            >
            <span class="block text-xs text-secondary"
              >{{ kind.name }} · {{ details }}</span
            >
          </span>
        </div>
        <ul role="menu" class="py-1">
          <li class="px-4 pb-1 pt-2 text-xs font-bold uppercase text-secondary">
            Also download as
          </li>
          <li v-for="format in others" :key="format" role="none">
            <button
              type="button"
              role="menuitem"
              class="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-primary hover:bg-gray-10"
              @click="save(format)"
            >
              {{ FILE_FORMATS[format].label }}
              <span class="text-xs text-secondary"
                >.{{ FILE_FORMATS[format].extension }}</span
              >
            </button>
          </li>
        </ul>
      </div>
    </transition>
    <p
      v-if="error"
      class="absolute right-0 top-full mt-2 w-60 text-right text-xs font-medium text-red-500"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>
